import { Component } from 'react'
import { connect } from 'react-redux'
import { NavLink } from 'react-router'
import { Button, Modal } from 'reactstrap'
import PropTypes from 'prop-types'

import { ElementWithTooltip } from '../../@paco/compositions'
import { ConnectedHelpTooltip } from '../../@paco/connectors'
import { doesPeriodOverlapWithLockedPayrollPeriod, getPayrollPeriodFromDate } from '../../@paco/entities/PayrollPeriod/PayrollPeriodHelpers'
import { transformToPeriod } from '../../@paco/entities/Period/PeriodTransformers'
import { transformLegacyRoleToRole } from '../../@paco/entities/Role/RoleTransformers'
import { getSettingEnabled } from '../../@paco/entities/Setting/SettingHelpers'
import checkPermission from '../../@paco/helpers/permissions/checkPermission'
import trans from '../../@paco/helpers/trans'
import { getCheckedFormOptionsValue } from '../../@paco/helpers/types/formOption'
import { exportTracks, getTracks } from '../../@paco/redux/tracks/tracksActions'
import { setFinishedTracksSorting } from '../../@paco/redux/tracks/tracksReducer'
import { ScheduleRoutes } from '../../@paco/routes/helpers'
import { PrivateRouteGroupRoutes } from '../../@paco/routes/routes'
import { HorizontalAlignment } from '../../@paco/types/alignmentTypes'

import ButtonAuthenticate from '../../components/ButtonAuthenticate/ButtonAuthenticate'
import ButtonHistory from '../../components/ButtonHistory/ButtonHistory'
import NewFilters from '../../components/Filters/Filters'
import ExportRangeForm from '../../components/forms/ExportRangeForm/ExportRangeForm'
import ItemsList from '../../components/ItemsList/ItemsList'
import SearchBar from '../../components/SearchBar/SearchBar'
import SidebarPage from '../../components/SidebarPage/SidebarPage'
import Icon from '../../components/style/Icon/Icon'
import TableContainer from '../../components/Table/TableContainer'
import UserPageLink from '../../components/UserPageLink/UserPageLink'
import { BREAK_MOBILE, TRACK_TYPE_FINISHED } from '../../constants'
import { getObjProperty, getUserFullName } from '../../helpers'
import {
    addHours,
    convertMinsToHrsMins,
    formatDate,
    getEndOfWorkDay,
    getStartOfWorkDay,
} from '../../helpers/date'
import { translate } from '../../helpers/translations/translator'
import { setEmployeeSearch, setFilter } from '../../redux/filter/filterActions'
import { setPaginationNumber } from '../../redux/pagination/paginationActions'
import { setStartAndEndDate } from '../../redux/weekNavigator/weekNavigatorActions'
import { transformPayrollPeriodToLegacyPayrollPeriod } from '../../services/PayrollPeriodService/transformPayrollPeriodLegacy'
import CalendarToolbar from '../Schedule/components/CalendarToolbar/CalendarToolbar'
import getPermissionToApproveTrack from './helpers/getPermissionToApproveTrack'
import { getShiftStartAndEndTimeOfTrack } from './helpers/getShiftStartAndEndTimeOfTrack'
import { renderInfoCircles, renderWorkedTotalHours } from './helpers/render'
import { setActiveTrackFromUrl } from './helpers/setActiveTrackFromUrl'

import './Tracks.scss'
import { TrackExportType } from '../../@paco/types'

class FinishedTracks extends Component {
    state = {
        tracks: [],
        exportForm: false,
        activeTrackId: undefined,
        legacyPayrollPeriods: [],
    }

    componentDidMount() {
        const { dispatch } = this.props

        const trackIdFromUrl = dispatch(setActiveTrackFromUrl())
        this.setState({ activeTrackId: trackIdFromUrl })

        this.getTracks()
    }

    componentDidUpdate(prevProps) {
        const activeDepartmentOptions = this.props.departmentOptions.filter(option => option.isChecked)
        const prevActiveDepartmentOptions = prevProps.departmentOptions.filter(option => option.isChecked)

        if (activeDepartmentOptions.length !== prevActiveDepartmentOptions.length) {
            this.getTracks()
        }

        if (this.props.startDate !== prevProps.startDate || this.props.endDate !== prevProps.endDate) {
            this.getTracks()
        }

        if (this.isMobile() && this.props.selectedDate !== prevProps.selectedDate) {
            this.getTracks()
        }

        if (this.props.filter.employeeSearch !== prevProps.filter.employeeSearch) {
            this.getTracks()
        }
    }

    isMobile = () => window.innerWidth <= BREAK_MOBILE

    static getDerivedStateFromProps(props, state) {
        const { finishedTracks, onReviewTrackClick } = props
        const { activeTrackId } = state

        if (props.tracks !== state.tracks) {
            const matchedTrack = finishedTracks.find(track => track.id === activeTrackId)

            if (matchedTrack) {
                onReviewTrackClick(matchedTrack, TRACK_TYPE_FINISHED)
            }
        }

        const activeTrackNotFound = !! (props.tracks.length || state.tracks.length) && ! finishedTracks.some(track => track.id === activeTrackId)
        const legacyPayrollPeriods = props.payrollPeriods.map(transformPayrollPeriodToLegacyPayrollPeriod)

        return {
            ...state,
            legacyPayrollPeriods,
            tracks: props.finishedTracks,
            activeTrackId: activeTrackNotFound ? undefined : state.activeTrackId,
        }
    }

    dispatchSetFilters = (filter) => {
        const { dispatch } = this.props
        dispatch(setFilter(filter, this.getTracks))
    }

    dispatchExportTracks = (startDate, endDate, payrollPeriodIds, userIds, exportType) => {
        const { dispatch } = this.props

        const departmentIds = getCheckedFormOptionsValue(this.props.departmentOptions)
        const employmentTypeIds = this.props.filter.employeeContractTypes
        const search = this.props.filter.employeeSearch

        const {
            employeeContractTypes,
        } = this.props.filter

        this.setState({
            exportForm: false,
        })

        dispatch(exportTracks({
            startDate,
            endDate,
            payrollPeriodIds,
            userIds,
            exportType,
            departmentIds,
            employmentTypeIds,
            search,
            employeeContractTypes,
        }))
    }

    setStartAndEndDate = (selectedDate, startDate, endDate) => {
        const { dispatch } = this.props
        dispatch(setStartAndEndDate(selectedDate, startDate, endDate))
    }

    onSearchBarChange = (value) => {
        const { dispatch } = this.props
        dispatch(setEmployeeSearch(value))
    }

    onSortButtonClick = (field) => {
        const { dispatch, finishedTracksSorting } = this.props
        const isSameColumn = finishedTracksSorting.active === field
        const direction = isSameColumn
            ? (finishedTracksSorting.direction === 'asc' ? 'desc' : 'asc')
            : (field === 'name' ? 'asc' : 'desc')
        const sorting = {
            active: field,
            direction,
        }

        dispatch(setFinishedTracksSorting(sorting))
        dispatch(setPaginationNumber(1))
        this.getTracks(1, sorting)
    }

    renderEditAndDeleteButtons = (track) => {
        const {
            permissions,
            currentUser,
            currentUserRole,
            onReviewTrackClick,
            onDeleteTrackClick,
            onShowLogsClick,
            payrollPeriods,
        } = this.props
        const legacyTrackOwnerRoles = getObjProperty(track, 'owner.roles') || []
        const trackOwnerId = getObjProperty(track, 'owner.id')
        const trackOwnerRoles = legacyTrackOwnerRoles.map(transformLegacyRoleToRole)

        const canEditTrack = getPermissionToApproveTrack(
            currentUserRole,
            trackOwnerRoles,
            permissions,
            trackOwnerId,
            currentUser.id,
        )
        const canDeleteTrack = checkPermission(permissions, 'delete-all-tracks')
        const canViewLogs = checkPermission(permissions, 'view-all-logs')
        const payrollPeriodError = doesPeriodOverlapWithLockedPayrollPeriod(
            transformToPeriod(new Date(track.billablePeriod.start), new Date(track.billablePeriod.end)),
            payrollPeriods,
        )

        return (
            <>
                {canEditTrack && (
                    <ElementWithTooltip
                        className="tracks__table-tooltip"
                        tooltipIsActive={!! payrollPeriodError}
                        tooltipText={translate('common.datesOverlapWithLockedPayrollPeriod')}
                    >
                        <button
                            type="button"
                            disabled={!! payrollPeriodError}
                            onClick={() => onReviewTrackClick(track, TRACK_TYPE_FINISHED)}
                            className="tracks__table-button"
                        >
                            <Icon className="tracks__table-button-icon" kind="edit" />
                        </button>
                    </ElementWithTooltip>
                )}
                {canDeleteTrack && (
                    <ElementWithTooltip
                        className="tracks__table-tooltip"
                        tooltipIsActive={!! payrollPeriodError}
                        tooltipText={translate('common.datesOverlapWithLockedPayrollPeriod')}
                    >
                        <button
                            type="button"
                            disabled={!! payrollPeriodError}
                            onClick={() => onDeleteTrackClick(track)}
                            className="tracks__table-button"
                        >
                            <Icon className="tracks__table-button-icon" kind="trash" />
                        </button>
                    </ElementWithTooltip>
                )}
                {canViewLogs && (
                    <ButtonHistory
                        onClick={() => onShowLogsClick(track.id)}
                    />
                )}
            </>
        )
    }

    getTracks = (page, sorting = this.props.finishedTracksSorting) => {
        const {
            dispatch,
            startDate,
            endDate,
            selectedDate,
            departmentOptions,
            filter,
        } = this.props

        const isMobile = this.isMobile()
        const effectiveStartDate = isMobile ? selectedDate : startDate
        const effectiveEndDate = isMobile ? selectedDate : endDate

        // Earliest shifts start at 04:00, so we add an extra threshold so we also get the tracks of employees who clock in early.
        const tracksStart = addHours(getStartOfWorkDay(effectiveStartDate), -1)

        dispatch(getTracks(
            TRACK_TYPE_FINISHED,
            {
                start: tracksStart,
                end: getEndOfWorkDay(effectiveEndDate),
            },
            page,
            departmentOptions,
            filter.employeeSearch,
            filter.employeeContractTypes,
            sorting,
        ))
    }

    renderTable() {
        const {
            finishedTracks,
            finishedTracksSorting,
            permissions,
            settings,
            loadingTrackIds,
        } = this.props
        const { activeTrackId } = this.state
        const isTimerEnableFeedback = getSettingEnabled('TIMER_ENABLE_FEEDBACK', settings)
        const canViewSatisfaction = isTimerEnableFeedback && checkPermission(permissions, 'view-all-feedback-ratings')
        const canViewSatisfactionFeedback = isTimerEnableFeedback && checkPermission(permissions, 'view-all-feedback')
        const canViewOfflineTrackIcon = checkPermission(permissions, 'view-offline-tracks')
        const isAscending = finishedTracksSorting.direction === 'asc'

        return (
            <TableContainer
                activeId={activeTrackId}
                data={finishedTracks}
                loadingIds={loadingTrackIds}
                attributes={[
                    {
                        customAction: track => (
                            <UserPageLink
                                isPlain
                                className="tracks-user-link"
                                name={getUserFullName(track.owner.person)}
                                id={track.owner.id}
                            />
                        ),
                        label: translate('common.name'),
                        type: 'sort',
                        ascending: isAscending,
                        active: finishedTracksSorting.active === 'name',
                        callback: () => this.onSortButtonClick('name'),
                    },
                    {
                        customAction: track => formatDate(track.checkInOutPeriod.start, 'd MMMM yyyy'),
                        label: translate('common.date'),
                        type: 'sort',
                        ascending: isAscending,
                        active: finishedTracksSorting.active === 'checkIn',
                        callback: () => this.onSortButtonClick('checkIn'),
                    },
                    {
                        customAction: (track) => {
                            const route = track.shift ? `${PrivateRouteGroupRoutes.shifts()}/${ScheduleRoutes.shift(track.shift.id)}` : undefined
                            const label = getShiftStartAndEndTimeOfTrack(track)

                            if (! route) {
                                return label
                            }

                            return (
                                <NavLink to={route}>
                                    {label}
                                </NavLink>
                            )
                        },
                        label: translate('common.shift'),
                    },
                    {
                        customAction: track => `${formatDate(track.billablePeriod.start, 'HH:mm')} - 
                        ${track.billablePeriod.end ? formatDate(track.billablePeriod.end, 'HH:mm') : translate('common.unknown')}`,
                        label: translate('common.track'),
                    },
                    {
                        customAction: track => `${convertMinsToHrsMins(track.deviation)} ${translate('common.hour').toLowerCase()}`,
                        label: translate('common.deviation'),
                        type: 'sort',
                        ascending: isAscending,
                        active: finishedTracksSorting.active === 'deviationMinutes',
                        callback: () => this.onSortButtonClick('deviationMinutes'),
                    },
                    {
                        customAction: track => renderWorkedTotalHours(track, settings, canViewOfflineTrackIcon),
                        label: translate('pages.tracks.workedTotal'),
                        type: 'sort',
                        ascending: isAscending,
                        active: finishedTracksSorting.active === 'billableMinutes',
                        callback: () => this.onSortButtonClick('billableMinutes'),
                    },
                    {
                        customAction: track => renderInfoCircles(track, canViewSatisfaction, canViewSatisfactionFeedback),
                        label: translate('pages.tracks.shiftInfo'),
                        className: 'td-info-circles',
                    },
                    {
                        customAction: track => this.renderEditAndDeleteButtons(track),
                        label: '',
                        className: 'tracks-td-edit-delete',
                    },
                ]}
                tableRowClassName="finished-tracks__row"
            />
        )
    }

    renderTracks() {
        const { legacyPayrollPeriods } = this.state
        const {
            loading,
            tracksLoading,
            eventLogsLoading,
            filter,
            finishedTracks,
            pagination,
            timeMode,
            exportCSVRequest,
        } = this.props
        const { employeeSearch } = filter

        return (
            <>
                <div className="tracks-overview-top">
                    <CalendarToolbar
                        timeMode={timeMode}
                        payrollPeriods={legacyPayrollPeriods}
                        setStartAndEndDate={this.setStartAndEndDate}
                        afterDateChange={this.afterDateChange}
                    />
                    <ConnectedHelpTooltip
                        index={0}
                        route="finished-tracks"
                        subTitle={trans('help.tracks.finishedTracks.title')}
                        text={trans('help.tracks.finishedTracks.text')}
                        title={trans('help.tracks.title')}
                        className="tracks__finished-tracks-help-tooltip"
                    />
                    <div className="tracks-overview-top-right">
                        <div>
                            <ConnectedHelpTooltip
                                index={4}
                                route="finished-tracks"
                                subTitle={trans('help.tracks.exportTracks.title')}
                                text={trans('help.tracks.exportTracks.text')}
                                title={trans('help.tracks.title')}
                                className="tracks__export-tracks-help-tooltip"
                            />
                            <Button
                                className="button-export tracks-button-export"
                                disabled={exportCSVRequest}
                                onClick={() => this.setState({ exportForm: true })}
                                color="orange"
                            >
                                <Icon color="white" kind="arrow_downward" />
                                {translate('common.export')}
                            </Button>
                        </div>
                        <SearchBar
                            placeholder={translate('common.searchEmployee')}
                            startVal={employeeSearch}
                            onChange={this.onSearchBarChange}
                        />
                    </div>
                </div>
                <ItemsList
                    isLoading={loading || tracksLoading || eventLogsLoading}
                    itemsLength={finishedTracks.length}
                    pagination={pagination}
                    emptyMessage={translate('pages.tracks.emptyList')}
                    onPagination={this.getTracks}
                >
                    {this.renderTable()}
                </ItemsList>
            </>
        )
    }

    render() {
        const { exportForm, legacyPayrollPeriods } = this.state
        const {
            filter,
            canAddTracks,
            payrollPeriods,
            employmentTypes,
            onAddTrackClick,
        } = this.props
        const period = getPayrollPeriodFromDate(new Date(), payrollPeriods)

        return (
            <div className="tracks">
                <SidebarPage
                    sidebarContent={(
                        <NewFilters
                            filter={filter}
                            employmentTypes={employmentTypes}
                            button={canAddTracks ? (
                                <ButtonAuthenticate onClick={onAddTrackClick} text={translate('pages.tracks.addTrack')} />
                            ) : null}
                            filterBlocks={[
                                'contract-types',
                            ]}
                            onChange={this.dispatchSetFilters}
                        />
                    )}
                >
                    <div className="tracks-list tracks-finished">
                        {this.renderTracks()}
                    </div>
                    <ConnectedHelpTooltip
                        index={1}
                        route="finished-tracks"
                        subTitle={trans('help.tracks.filters.title')}
                        text={trans('help.tracks.filters.text')}
                        title={trans('help.tracks.title')}
                        showMobileInfoWarning
                        className="tracks__filters-help-tooltip"
                    />
                    <ConnectedHelpTooltip
                        index={2}
                        videoSrc="/help/finished-tracks-icons.webm"
                        route="finished-tracks"
                        subTitle={trans('help.tracks.trackIcons.title')}
                        text={trans('help.tracks.trackIcons.text')}
                        title={trans('help.tracks.title')}
                        showMobileInfoWarning
                        className="tracks__finished-track-icons-help-tooltip"
                    />
                    <ConnectedHelpTooltip
                        index={3}
                        route="finished-tracks"
                        subTitle={trans('help.tracks.editTrack.title')}
                        text={trans('help.tracks.editTrack.text')}
                        title={trans('help.tracks.title')}
                        showMobileInfoWarning
                        alignment={HorizontalAlignment.right}
                        className="tracks__edit-track-help-tooltip"
                    />
                </SidebarPage>
                <Modal size="lg" isOpen={exportForm} className="form-export-range">
                    <ExportRangeForm
                        title={translate('pages.tracks.exportTracks')}
                        intro={translate('common.exportFilterCaution')}
                        defaultExportType={TrackExportType.tracks}
                        onSubmit={this.dispatchExportTracks}
                        onCancel={() => this.setState({ exportForm: false })}
                        activePeriod={period && transformPayrollPeriodToLegacyPayrollPeriod(period)}
                        startDate={period?.period.start}
                        endDate={period?.period.end}
                        payrollPeriods={legacyPayrollPeriods}
                    />
                </Modal>
            </div>
        )
    }
}

FinishedTracks.propTypes = {
    loading: PropTypes.bool.isRequired,
    tracksLoading: PropTypes.bool.isRequired,
    pagination: PropTypes.object.isRequired,
    finishedTracks: PropTypes.array.isRequired,
    finishedTracksSorting: PropTypes.shape({
        active: PropTypes.string.isRequired,
        direction: PropTypes.oneOf(['asc', 'desc']).isRequired,
    }).isRequired,
    settings: PropTypes.array.isRequired,
    dispatch: PropTypes.func.isRequired,
    filter: PropTypes.object.isRequired,
    timeMode: PropTypes.string.isRequired,
    startDate: PropTypes.object.isRequired,
    endDate: PropTypes.object.isRequired,
    selectedDate: PropTypes.object.isRequired,
    onAddTrackClick: PropTypes.func.isRequired,
    onReviewTrackClick: PropTypes.func.isRequired,
    onDeleteTrackClick: PropTypes.func.isRequired,
    onShowLogsClick: PropTypes.func.isRequired,
    exportCSVRequest: PropTypes.bool.isRequired,
    permissions: PropTypes.array.isRequired,
    payrollPeriods: PropTypes.array.isRequired,
    employmentTypes: PropTypes.array.isRequired,
    currentUser: PropTypes.object.isRequired,
    currentUserRole: PropTypes.object.isRequired,
    canAddTracks: PropTypes.bool.isRequired,
    eventLogsLoading: PropTypes.bool.isRequired,
    loadingTrackIds: PropTypes.array.isRequired,
}

export default connect()(FinishedTracks)
